import BaseNodeModel from "./BaseNodeModel";
import { Constant } from '../const';
import ModuleUsedIn from './../utilities/usedIn/ModuleUsedIn';
import DesignerSetting from "./../DesignerSetting";
import { resetCommonModuleLocalVariablesNotSensitive, updateLastChange } from "../utilities";

export default class ModuleNodeModel extends BaseNodeModel {
    /**
     * Module node model
     * @module ModuleNodeModel
     * @param {string} nodeName Node display name
     * @param {number} x Node x coordinate on canvas
     * @param {number} y Node y coordinate on canvas
     * @param {Object} props Pre-defined node properties; if non-null, apply values to node properties
     * @param {string} id If non-null, use this ID as the node ID
     */
    constructor(nodeName = "UNTITLED_NODE", x = 0, y = 0, props = null, id = null) {
        super(nodeName, x, y, null, id);
        this.props.nodeTypeName = "Module";
        this.props.colorClass = "cyan";
        this.props.iconClass = "package";
        this.DEFAULT_COLOR_CLASS = "cyan";
        this.type = "module";
        this.applyProps(props);
    }

    getProperty(key) {
        if (key === "refModId") {
            if (this.getProperty("ref")) {
                return this.getProperty("ref").split("::")[0];
            }
            return null;
        }
        return super.getProperty(key);
    }

    setVariables(vars, modId, usedId, op) {
        super.setVariables(vars, modId, usedId, op);
        this.updateRefModLocalRef(vars, window.apm.getCurrentDiagramWidget().props.node.id, this.getProperty("refModId"), op);
    }

    setConfig(key, value) {
        if (key === "name") {
            this.name = value;
        } else if(key === "inputs" || key === "outputs") {
            const inputs = this.props.config.inputs;
            const isToggleVariableType = value.action && value.action === 'toggle-variable-reference';                
            const isResetToDefault = value.action && value.action === 'reset-default';                
            
            const oldInputs = this.getConfig("inputs") ? JSON.parse(JSON.stringify(this.getConfig("inputs"))) : [];
            const oldOutputs = this.getConfig("outputs") ? JSON.parse(JSON.stringify(this.getConfig("outputs"))) : [];
            var isInstanceNode = this.id !== this.props.parentComponentID
            
            if (key === "inputs") {
                const input = inputs[value.inputIndex];

                if (isToggleVariableType) {
                    // toggle-variable-reference is an item in the KeyValue KeyedLabelMenuList
                    if(input.type === 'value'){
                        input.type = 'variable';
                        input.value = '';
                        input.varName ??= '';
                    } else {
                        input.type = 'value';
                        input.value ??= '';
                        input.varName = '';
                    }
                } else if(isResetToDefault) {
                    // reset-default is an item in the KeyValue KeyedLabelMenuList
                    input.type = 'value';
                    input.value = input.defaultValue;
                    input.varName = '';
                } else {
                    // otherwise value changed
                    const isTypeVariable = (
                        typeof value.value === 'string'
                        && (
                            value.value?.startsWith('app.')
                            || value.value?.startsWith('mod.')
                        )
                    );

                    if(isTypeVariable){
                        input.type = 'variable';
                        input.value = '';
                        input.varName = value.value
                    } else {
                        input.type = 'value';
                        input.value = value.value;
                        input.varName = '';

                        if(!isInstanceNode){
                            // if application module then conditionally update default value value
                            if(input.value === input.defaultValue){
                                input.defaultValue = value.value;                                                              
                            }
                        }
                    }
                }
                
            }
            if (key === "outputs") {
                this.props.config[key] = value;
                super.updateVariables(oldInputs, oldOutputs);
            }
        } else if(key === 'version'){
            this.props.version = value;
            this.props.config.version = value;
            
            window.apm.postEventToAllViews({ target: "request-refresh", event: { id: this.id, type: "node", upDatedField: "version", props: this.props } });
            // CM TODO: this doesn't trigger changeMaps

        }  else if(key === 'sensitiveOverrides'){
            const serialized = window.apm.serialized;
            const parentID = this.props.parentComponentID;
            
            resetCommonModuleLocalVariablesNotSensitive(parentID,serialized);
            
            this.props.config[key] = {
                enabled: value,
                lastLocalChange: window.apm.timestamp
            };
        } else {
            this.props.config[key] = value;
        }
        updateLastChange(this.props);
        document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        window.apm.setSerializedChanged();
    }
    
    getConfig(key, inputProps = null) {
        if (key === "name") {
            return this.name;
        }
        if (key === "inputs" && inputProps) {
            return this.props.config[key][inputProps.inputIndex];
        }
        if(key === "sensitiveOverrides"){
            return this.props.config[key]["enabled"];    
        }
        return this.props.config[key];

    }
    /**
     * Update reference module local variable reference
     * @param vars
     * @param usedId
     * @param modId
     * @param op
     */
    updateRefModLocalRef(vars, usedId, modId, op) {
        for (var i = 0; i < vars.length; i++) {
            const v = vars[i];
            if (v.argumentName && v.argumentName.indexOf(".") !== -1) {
                const varName = v.argumentName.split(".")[1];
                if (op === "set") {
                    window.apm.setVariable(varName, v.type, usedId, modId);
                }
                if (op === "del") {
                    window.apm.removeVariable(varName, usedId, modId);
                }
            }
        }
    }
    getOutputSuggestions() {
        const suggestion = super.getOutputSuggestions();
        var exitIndex;
        var values = [];
        var moduleIndex = window.apm.serialized.components.modules.findIndex(mo => mo.id === this.props.parentComponentID);
        if (moduleIndex !== -1) {
            var moduleExitNodes = window.apm.serialized.components.modules[moduleIndex].versions[1].flow.nodes.filter(ex => ex._class === "ExitNodeModel");
            if (moduleExitNodes.length > 0) {
                for (var ex = 0; ex < moduleExitNodes.length; ex++) {
                    if (moduleExitNodes[ex].props.config.outputs && moduleExitNodes[ex].props.config.outputs.length > 0) {
                        if (moduleExitNodes[ex].props.config.outputs[0].type === "VALUE") {
                            exitIndex = values.findIndex(el => el === moduleExitNodes[ex].props.config.outputs[0].value);
                            if (exitIndex === -1) values.push(moduleExitNodes[ex].props.config.outputs[0].value);
                        }
                        else if (moduleExitNodes[ex].props.config.outputs[0].type === "INTENT") {
                            var intent = window.apm.getNode(moduleExitNodes[ex].props.config.outputs[0].intent);
                            if (intent) {
                                exitIndex = values.findIndex(el => el === intent.name);
                                if (exitIndex === -1) values.push(intent.name);
                            }
                        }
                        else if (moduleExitNodes[ex].props.config.outputs[0].type === "VARIABLE") {
                            const sv = moduleExitNodes[ex].props.config.outputs[0].varName.split(".");
                            var v = {};
                            if (sv[0] === Constant.k.SCOPE_APPLICATION) {
                                v = window.apm.getVariable(sv[1]);
                            }
                            if (sv[0] === Constant.k.SCOPE_MODULE) {
                                v = window.apm.getVariable(sv[1], this.props.parentComponentID);
                            }
                            if (v && v.name) {
                                exitIndex = values.findIndex(el => el === v.name);
                                if (exitIndex === -1) values.push(v.name);
                            }
                        }
                    }
                }
            }
        }
        suggestion.value = values;

        return suggestion;
    }

    getEnableDisabledModuleSwitch(mod) {
        // DEVDES2-1023: Exclude disabled modules in Flows from UsedIn count
        const usedIn = ModuleUsedIn.getModuleUsedIn(mod, window.apm.serialized, { excludeDisabledInFlow: true });
        return [...Object.values({ ...usedIn })].filter(arr => arr.length > 0).length === 0;
    }

    /**
     * when toggling an application-level module from disabled to enabled, descendent application-level modules must also be toggled to enabled
     */
    toggleDisabledModules() {

        if (this.context.props.config.disabledInFlow) {
            return;
        }

        // recursive function to enable descendant modules of enabled modules
        const enableModulesInFlow = (modulesInFlow) => {
            modulesInFlow.forEach(mod => {
                const parentModule = window.apm.serialized.components.modules.find(pm => pm.id === mod.props.parentComponentID);
                const mif = parentModule.versions[1].flow.nodes.filter(n => n._class === 'ModuleNodeModel');

                parentModule.props.config['disabledInFlow'] = false;
                if (mif.length > 0) {
                    enableModulesInFlow(mif);
                }
            });
        };
        const modulesInFlow = window.apm.getNode(this.context.id).versions[1].flow.nodes.filter(n => n._class === 'ModuleNodeModel');

        // start recursion with modules in this context's flow
        enableModulesInFlow(modulesInFlow);
        window.apm.setSerializedChanged();
        // update jstree
        window.apm.refreshApplicationTree();
    }

    getSensitiveOverrides() {
        // TODO: refactor?
        return this.props.config.sensitiveOverrides && this.props.config.sensitiveOverrides.enabled ? this.props.config.sensitiveOverrides.enabled : false;
    }


    getSuggestionBasedOnType(varType, inputType) {
        if (varType === "STRING"&& inputType === "variable") return "variable";
        else if (varType === "PROMPT" && inputType === "value") return "prompt";
        else if (varType === "PROMPT" && inputType === "variable") return "variable";
        else if (varType === "MODULE" && inputType === "value") return "module";
        else if (varType === "MODULE" && inputType === "variable") return "variable";
        else return "";
    }
    getValue(input) {
        if (input.type === "value") return input.value;
        else if (input.type === "variable") return input.varName;
        else return "";
    }
    

    /**
     * Return inputs for module instances. If in a Common Module app, return rootModule inputs 
     */
    getInputs(){
        const isCommonModuleApp = window.apm.serialized.isCommonModule || false;
        const isApplicationModule = this.id === this.props.parentComponentID || !this.props.parentComponentID;
        const isRootModule = this.props.parentComponentID === 'rootModule';
        const rootModule = window.apm.serialized.components.modules.find(mod => mod.id === 'rootModule');

        // inputs for all modules, including root
        if(isCommonModuleApp){
            if(isRootModule){
                return window.apm.getModuleInputs(rootModule);
            } else if(!isApplicationModule){
                return this.props.config.inputs ? this.props.config.inputs : [];    
            }
            
        // inputs only for canvas modules
        } else if(!isApplicationModule){
            return this.props.config.inputs ? this.props.config.inputs : [];
        }
    }
    
    openNewLocalVariableDialog(){
        window.apm.getCurrentDiagramWidget().openNewLocalVariableDialog({isLocalVar: true, useModule: this.id});
    }

    getConfigViews(type = 'default', isCommonModule=false) {
        const readOnly = window.location.hash === "#intents" ? true : false;
        const sensitiveOverridesEnabled = this.props.config?.sensitiveOverrides?.enabled;

        const keyedLabelOptions = [
            { name: "Revert to default value", action: "reset-default" },
            { name: "Use variable reference", action: "toggle-variable-reference" }
        ];

        var inputConfigViews = [];
        const inputs = this.props.config.inputs || [];
        inputs.sort((a,b) => (a.argumentName < b.argumentName) ? -1: (a.argumentName > b.argumentName) ? 1 : 0);
        inputs.map((input, i) => {
            const variableName = input.argumentName.substring(4, input.argumentName.length);
            const variable = window.apm.getVariable(variableName, this.props.parentComponentID);
            if (variable) {
                const varType = variable.props.config.type;
                const label = <div><span>{variableName}</span> <span style={{ color: "var(--color-text-lighter)" }}>{"(" + varType + ")"}</span></div>
                const value = this.getValue(input)
                const suggestion = this.getSuggestionBasedOnType(varType, input.type);
                const pair = {
                    key: input.type,
                    value: value
                }
                
                const disabled = input.type === "none" ? true : false;
                switch (varType) {
                    case "STRING":
                    case "ARRAY":
                    case "OBJECT":
                    case "NUMBER":
                    case "PROMPT":
                    case "MODULE":
                        inputConfigViews.push({
                            type: "KeyValue", 
                            key: i, 
                            variableName: variableName,
                            layout: 'keyedLabel',
                            inputIndex: i,
                            pair:pair,
                            pairKeyView: {
                                type: "menu",
                                key: "key",
                                optionName: this.getPropLabel("type"),
                                options: (input.type === 'variable') ? [keyedLabelOptions[0]] : keyedLabelOptions
                            },
                            pairValueView: {
                                type: "Input",
                                key: "value",
                                value: value,
                                disabled: disabled,
                                suggestion: suggestion,
                                input:input,
                                defaultValue: variable.props.config.defaultValue || '',
                                variable: variable
                            }
                        })
                        break;
                    case "BOOLEAN":
                        inputConfigViews.push({
                            type: "KeyValue", 
                            key: i, 
                            label: label,
                            layout: 'keyedLabel',
                            variableName: variableName, 
                            inputIndex: i,
                            pair:pair,
                            pairKeyView: {
                                type: "menu",
                                key: "key",
                                optionName: this.getPropLabel("type"),
                                options: (input.type === 'variable') ? [keyedLabelOptions[0]] : keyedLabelOptions
                            },
                            pairValueView: {
                                type: "Switch",
                                key: "value",
                                value: value,
                                disabled: disabled,
                                suggestion: suggestion,
                                input:input,
                                defaultValue: variable.props.config.defaultValue || '',
                                variable: variable
                                
                            }
                        })
                        break;
                }
            }
        });
        
        if (!this.getProperty("ref")) {
            this.props.ref = this.props.parentComponentID ? this.props.parentComponentID : this.id;
        }
        const tooltipText = (this.props.parentComponentID && this.props.parentComponentID !== this.id) ? 'To modify a module\'s description, select the module from the Application panel' : null;
        var configViews = [];
        if(type === 'default'){
            configViews.push(                
                ...this.getConfigViewNameField(),
                ...[{ 
                        type: "Textarea", 
                        tooltip: tooltipText, 
                        label: this.getPropLabel("description"), 
                        key: "description", 
                        valRules: { "noEmpty": true }},
                { type: "hr" }]
            );
        
        const CommonModuleVersionDropDownTooltipText = (this.props.parentComponentID && this.props.parentComponentID !== this.id) ? 'To modify a common module\'s version, select the module from the Application panel' : null;
        // Show property panel only for common modules and nested common modules. Both should hvae isRootModule as true, if not it is just a module in common module
        if(DesignerSetting.s.is_public === "false" &&!this.props.embed && this.props.moduleApp&&this.props.isRootModule ){
                
            configViews.push(
                    {   type: "CommonModuleVersionDropDown",
                        popupFunc: window.apm.openCommonModuleChangeVersionWindow, 
                        key: "version",
                        label: this.getPropLabel("version"),
                        optionName: this.getPropLabel("version"),
                        removable: false,
                        readOnly: false,
                        tooltip: CommonModuleVersionDropDownTooltipText
                    },
                    { type: "hr" }
                )
            
        }

        if(isCommonModule && sensitiveOverridesEnabled){

            configViews.push(
                {   type: "Switch",
                    key: "sensitiveOverrides",
                    label: "Redaction Enabled",
                    tooltip: "Toggles Sensitivity Data Override for Common Modules",
                    checked: !!this.getSensitiveOverrides(),
                    disabled: this.props.config.sensitiveOverrides.enabled === false
                },
                { type: "hr" }
            );
        }
    
        if(this.props.parentComponentID === this.id){
            configViews.push({type: "ItemList", 
                        label: "Module Inputs", 
                        view: "moduleInputs", 
                        callback: () => {this.openNewLocalVariableDialog()}    
                    }); 
        } else {
            if(inputConfigViews.length > 0){
                configViews.push({
                    type: 'Label',
                    labelName: 'Module Inputs'
                })
            }
        }
        
        if(inputConfigViews.length > 0){
            configViews.push(...inputConfigViews);
            configViews.push({ type: "hr" });
        }
        
         configViews.push({ 
             type: "ItemList", 
             label: this.getPropLabel("outputs"), 
             tooltip: "At the end of the execution of this child module, local variable values of the child module may pass through to the specified variables of this parent module",
             key: "outputs", 
             view: "moduleReturn", 
             pairKeyView: { 
                 ref: this.getProperty("ref") },
                 readOnly:readOnly 
            });


        // only apply this view to application-level modules
        if (this.props.parentComponentID === this.id) {
            // only apply this view to application-level modules
            if (this.props.parentComponentID === this.id) {
                if (!window.apm.serialized.displayPrefs['hideDisabledElements']) {
                    configViews.push({
                        type: "Switch",
                        key: "disabledInFlow",
                        label: "Disable this module",
                        toggledLabel: "Enable this module",
                        disabled: !this.getEnableDisabledModuleSwitch(this),
                        checked: this.props.config.disabledInFlow,
                        callback: this.toggleDisabledModules
                    });
                }
            }
    
            
            configViews.push(
                ...[
                    { type: "hr" },
                    { type: "Textarea", label: this.getPropLabel("note"), key: "note", readOnly: readOnly }
                ]
            );
        }
    }
        if(type === 'commonModuleDefaults'){
            configViews.push(
                ...this.getConfigViewNameField(),
                ...[
                    { 
                        type: "Textarea", 
                        tooltip: tooltipText, 
                        label: this.getPropLabel("description"), 
                        key: "description", 
                        valRules: { "noEmpty": true }, readOnly: "true" }
                    ]
            );
    
                configViews.push(...inputConfigViews);
    
        }
        
        return configViews;
    }
}
